import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import { CopyIcon } from '@wheelbox/lithejs-lucide';

import { highlightCode, isHighlighterReady } from '@lib/syntax-highlighter';

export interface CodeBlockProps {
  id?: string;
  testId?: string;
  code: string;
  language?: string;
  filename?: string;
  showLineNumbers?: boolean;
  highlightLines?: number[];
  class?: string;
}

const CodeBlock: LitheFC<CodeBlockProps> = ({
  id = 'code-block',
  testId = 'code-block',
  code,
  language = 'typescript',
  filename,
  showLineNumbers = true,
  highlightLines = [],
  class: className = '',
}): LitheElement<'div'> => {
  const codeContainerId = `${id}-code-container`;
  const copyButtonId = `${id}-copy-btn`;

  const escapeHtml = (text: string): string => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
  };

  const formatCodeWithLineNumbers = (rawCode: string): string => {
    const lines = rawCode.split('\n');
    return lines
      .map((line, index) => {
        const lineNum = index + 1;
        const isHighlighted = highlightLines.includes(lineNum);
        const lineClass = isHighlighted
          ? 'bg-primary/10 border-l-2 border-primary -ml-4 pl-4'
          : '';

        if (showLineNumbers) {
          return `<div class="table-row ${lineClass}">
            <span class="table-cell pr-4 text-right text-muted-foreground/50 select-none w-8 text-xs">${lineNum}</span>
            <span class="table-cell">${escapeHtml(line) || ' '}</span>
          </div>`;
        }
        return `<div class="${lineClass}">${escapeHtml(line) || ' '}</div>`;
      })
      .join('');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      const button = document.getElementById(copyButtonId);
      if (button) {
        // Show checkmark
        button.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-400"><path d="M20 6 9 17l-5-5"/></svg>';
        button.classList.add('text-green-400');

        setTimeout(() => {
          if (button) {
            // Restore copy icon
            button.innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
            button.classList.remove('text-green-400');
          }
        }, 2000);
      }
    } catch {
      // Failed to copy to clipboard
    }
  };

  const applyHighlighting = (container: HTMLElement) => {
    if (isHighlighterReady()) {
      // Use Shiki for syntax highlighting
      const highlighted = highlightCode(code, language);

      // If we have line numbers, we need to process the Shiki output
      if (showLineNumbers) {
        // Parse the Shiki output and add line numbers
        const wrapper = document.createElement('div');
        wrapper.innerHTML = highlighted;

        const shikiPre = wrapper.querySelector('pre');
        if (shikiPre) {
          const shikiCode = shikiPre.querySelector('code');
          if (shikiCode) {
            // Get the lines from Shiki output
            const lines = shikiCode.innerHTML.split('\n');
            const numberedLines = lines
              .map((line, index) => {
                const lineNum = index + 1;
                const isHighlighted = highlightLines.includes(lineNum);
                const lineClass = isHighlighted
                  ? 'bg-primary/10 border-l-2 border-primary'
                  : '';

                return `<div class="table-row group ${lineClass}">
                  <span class="table-cell pr-4 text-right text-muted-foreground/40 select-none w-10 text-xs sticky left-0 bg-inherit">${lineNum}</span>
                  <span class="table-cell pl-2">${line || ' '}</span>
                </div>`;
              })
              .join('');

            shikiCode.innerHTML = numberedLines;
            shikiCode.classList.add('table', 'w-full');
          }

          // Apply styling to pre element
          shikiPre.classList.add(
            '!bg-transparent',
            'text-sm',
            'leading-relaxed',
            '!m-0',
            '!p-0'
          );

          container.innerHTML = shikiPre.outerHTML;
        }
      } else {
        // No line numbers - just use Shiki output directly
        container.innerHTML = highlighted;
        const pre = container.querySelector('pre');
        if (pre) {
          pre.classList.add(
            '!bg-transparent',
            'text-sm',
            'leading-relaxed',
            '!m-0',
            '!p-0'
          );
        }
      }
    } else {
      // Fallback to plain text with line numbers
      container.innerHTML = `<pre class="text-sm font-mono"><code class="language-${language} ${
        showLineNumbers ? 'table w-full' : ''
      }">${formatCodeWithLineNumbers(code)}</code></pre>`;
    }
  };

  const onMountHandler = (element: HTMLElement) => {
    // Use requestAnimationFrame to ensure children are rendered
    requestAnimationFrame(() => {
      const container = element.querySelector(
        `#${codeContainerId}`
      ) as HTMLElement;
      if (container) {
        // Apply highlighting immediately if ready, otherwise use fallback
        applyHighlighting(container);

        // If highlighter wasn't ready, retry after a short delay
        if (!isHighlighterReady()) {
          const retryTimeout = setTimeout(() => {
            if (isHighlighterReady()) {
              applyHighlighting(container);
            }
          }, 500);

          // Cleanup on unmount would go here if needed
          return () => clearTimeout(retryTimeout);
        }
      }
    });
  };

  return [
    'div',
    {
      id,
      'data-testid': testId,
      class: `code-block rounded-lg overflow-hidden border border-border bg-muted/30 dark:bg-slate-900 ${className}`,
      onmount: onMountHandler,
    },
    // Header (optional filename)
    filename
      ? [
          'div',
          {
            class:
              'flex items-center justify-between px-4 py-2 bg-muted/50 dark:bg-slate-800 border-b border-border',
          },
          [
            'span',
            { class: 'text-sm text-muted-foreground font-mono' },
            filename,
          ],
          [
            'button',
            {
              id: copyButtonId,
              onclick: copyToClipboard,
              class:
                'p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors',
              'aria-label': 'Copy code',
            },
            CopyIcon({ size: 16 }),
          ],
        ]
      : [
          'div',
          {
            class:
              'flex justify-end px-4 py-2 bg-muted/50 dark:bg-slate-800 border-b border-border',
          },
          [
            'button',
            {
              id: copyButtonId,
              onclick: copyToClipboard,
              class:
                'p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors',
              'aria-label': 'Copy code',
            },
            CopyIcon({ size: 16 }),
          ],
        ],
    // Code content container
    [
      'div',
      {
        id: codeContainerId,
        class: 'p-4 overflow-x-auto',
      },
      // Initial loading state - will be replaced by highlighted code
      [
        'pre',
        { class: 'text-sm font-mono text-foreground' },
        [
          'code',
          {
            class: `language-${language} ${
              showLineNumbers ? 'table w-full' : ''
            }`,
          },
          // Placeholder content while loading
          code,
        ],
      ],
    ],
  ];
};

export default CodeBlock;
